import { Box, Typography, useTheme } from '@mui/material';
import { useFormikContext } from 'formik';
import { Clock, Location } from 'iconsax-react';
import type { FC } from 'react';

import type { ProjectFormikType } from '@core/formik/project';
import type Enums from '@core/types/api/Enums';
import { getPlantDistanceInfo } from '@core/utils/projectHelpers';

export const LocationsSummarySection: FC = () => {
  const theme = useTheme();
  const formikBag = useFormikContext<ProjectFormikType>();

  const plants = formikBag.values.productsByPlants;

  if (plants.length === 0) {
    return (
      <Box>
        <Typography sx={{ fontSize: 20 }} variant='h6'>
          Summary of locations selected in Product(s) Info section
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ color: theme.palette.text.secondary }}>
            No locations selected yet
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 20, mb: 2 }} variant='h6'>
        Summary of locations selected in Product(s) Info section
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          border: '1px solid #EAEDF9',
          borderRadius: '10px',
        }}
      >
        {plants.map((location, index) => {
          const plantName = location.plant?.option?.label ?? 'Unknown location';

          const plantDistanceInfo = getPlantDistanceInfo({
            plantDriveTime: location.plantDriveTime,
            plantDistance: location.plantDistance,
            plantDistanceUnit: location.plantDistanceUnit as Enums.Unit | null,
          });

          return (
            <Box
              key={location.plant.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderBottom: index < plants.length - 1 ? `1px solid #EAEDF9` : 'none',

                [theme.breakpoints.down('sm')]: {
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 1,
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: 14,
                    minWidth: '85px',
                  }}
                >
                  Location {index + 1}
                </Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 400 }}>{plantName}</Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, minWidth: '200px' }}>
                {plantDistanceInfo?.distanceText && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Location size={16} color={theme.palette.text.secondary} />
                    <Typography sx={{ color: theme.palette.text.secondary, fontSize: 14 }}>
                      {plantDistanceInfo.distanceText}
                    </Typography>
                  </Box>
                )}
                {plantDistanceInfo?.durationText && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Clock size={16} color={theme.palette.text.secondary} />
                    <Typography sx={{ color: theme.palette.text.secondary, fontSize: 14 }}>
                      {plantDistanceInfo.durationText}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
